// src/pages/Dashboard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  ButtonBase,
  Box,
  useTheme,
  useMediaQuery,
  Paper,
  Stack
} from "@mui/material";

// استيراد الأيقونات 
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import WavingHandIcon from '@mui/icons-material/WavingHand';

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userName = localStorage.getItem("userName") || "";
  const companyName = localStorage.getItem("companyName") || t('dashboard.title');

  const shortcuts = [
    { 
      name: t('dashboard.purchases'), 
      icon: <ShoppingCartIcon />, 
      color: '#EF4444', 
      bgColor: 'rgba(239, 68, 68, 0.06)', 
      link: "/purchases-menu" 
    },
    { 
      name: t('dashboard.sales'), 
      icon: <ShoppingBagIcon />, 
      color: '#F59E0B', 
      bgColor: 'rgba(245, 158, 11, 0.06)', 
      link: "/sales-menu" 
    },
    { 
      name: t('dashboard.warehouses'), 
      icon: <WarehouseIcon />, 
      color: '#10B981', 
      bgColor: 'rgba(16, 185, 129, 0.06)', 
      link: "/warehouses-menu" 
    },
    { 
      name: t('dashboard.accounting'), 
      icon: <AccountBalanceIcon />, 
      color: '#3B82F6', 
      bgColor: 'rgba(59, 130, 246, 0.06)', 
      link: "/accounts-menu" 
    },
    { 
      name: t('dashboard.definitions'), 
      icon: <CategoryIcon />, 
      color: '#6366F1', 
      bgColor: 'rgba(99, 102, 241, 0.06)', 
      link: "/company-definitions" 
    },
    { 
      name: t('dashboard.pos'), 
      icon: <PointOfSaleIcon />, 
      color: '#8B5CF6', 
      bgColor: 'rgba(139, 92, 246, 0.06)', 
      link: "/pos" 
    },
    { 
      name: t('dashboard.generalSettings'), 
      icon: <TuneIcon />, 
      color: '#EC4899', 
      bgColor: 'rgba(236, 72, 153, 0.06)', 
      link: "/settings-menu" 
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 5, mb: 5, px: { xs: 2, md: 4 } }}>

      {/* هيدر الترحيب العصري */}
      <Box 
        sx={{ 
          mb: 5, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', // توسيط الهيدر ليتناسق مع الأبعاد الجديدة
          textAlign: 'center'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <WavingHandIcon sx={{ color: '#F59E0B', fontSize: isMobile ? '1.3rem' : '1.8rem' }} />
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {userName ? `مرحباً بك، ${userName}` : 'مرحباً بك، أحمد'}
          </Typography>
        </Stack>
        
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            lineHeight: 1.4
          }}
        >
          {companyName}
        </Typography>
      </Box>

      {/* شبكة الاختصارات المحدثة والذكية لجميع أنواع الشاشات */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',          // في الجوالات الصغيرة: كرتين بالصف لسهولة اللمس والقراءة
            sm: 'repeat(3, 1fr)',          // في الجوالات الكبيرة والتابلت: 3 كروت بالصف
            md: 'repeat(4, 1fr)',          // في شاشات اللابتوب والمتصفح المتوسط: 4 كروت فقط بالصف
            lg: 'repeat(4, 1fr)',          // في الشاشات المكتبية الكبيرة: تثبيت 4 كروت بالصف كما طلبت
          },
          maxWidth: '1100px',              // حصر العرض الأقصى للشبكة ليكون توزيع الـ 4 كروت متناسق وجميل بالمنتصف
          margin: '0 auto'                 // توسيط الشبكة بالكامل في الشاشات العريضة
        }}
      >
        {shortcuts.map((item, i) => (
          <ButtonBase
            key={i}
            onClick={() => navigate(item.link)}
            component={Paper}
            elevation={0}
            sx={{
              width: '100%',
              p: { xs: 3, sm: 4 },
              borderRadius: '24px', 
              backgroundColor: '#FFFFFF',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              
              "&:hover": { 
                boxShadow: `0 20px 25px -5px ${item.bgColor}, 0 10px 10px -5px rgba(0, 0, 0, 0.01)`, 
                transform: 'translateY(-6px)', 
                borderColor: item.color,
                "& .icon-wrapper": {
                  transform: 'scale(1.08) rotate(3deg)',
                  backgroundColor: item.color,
                  color: '#ffffff'
                }
              },
              "&:active": { transform: 'translateY(-2px)' }
            }}
          >
            {/* حاوية الأيقونة الدائرية التفاعلية */}
            <Box 
              className="icon-wrapper"
              sx={{ 
                width: { xs: 55, sm: 65 }, 
                height: { xs: 55, sm: 65 }, 
                borderRadius: '18px', 
                backgroundColor: item.bgColor, 
                color: item.color,
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                mb: 2,
                transition: 'all 0.3s ease'
              }}
            >
              {React.cloneElement(item.icon, { sx: { fontSize: { xs: 26, sm: 30 } } })}
            </Box>

            {/* اسم القسم */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                color: '#1E293B',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                letterSpacing: '-0.3px'
              }}
            >
              {item.name}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Container>
  );
}